// Лента изменений на телефоне (M7).
//
// Отличие от десктопной ленты не в вёрстке, а в жесте. На десктопе «прочитано»
// — отдельная кнопка у каждой записи, потому что мышью легко попасть в мелкую
// цель и потому что читать ленту там можно, ничего не открывая. На телефоне
// такой кнопки нет: палец делает одно движение, и оно означает «беру в работу»
// — тап по карточке помечает её прочитанной и сразу открывает заметку (M7.3).
// Массовое действие остаётся ровно одно — «прочитано» на всю группу дня (M7.1).
//
// Счётчик непрочитанного рисует таб-бар из `appStore.unread`, а не эта ветка.
// Поэтому свежее число из ответа сервера обязательно кладём в стор: без этого
// бейдж на «Ленте» не гаснет, даже когда карточки уже без точек. Считать
// самим нельзя — сервер знает и про записи за пределами выдачи, поэтому своё
// вычитание здесь только предварительное: окончательное число приходит с
// ответом на отметку, и кладёт его в стор `offline.ts` — отметка уезжает
// очередью, чтобы пережить офлайн (M10.4).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError, notesApi } from '../api'
import type { FeedItem, NoteSource } from '../protocol'
import { useAppStore } from '../store/appStore'
import { queueAction } from './offline'
import './discover.css'

const LIMIT = 100

const CHANGE_LABEL: Record<FeedItem['change'], string> = {
  created: 'создана',
  updated: 'изменена',
}

const SOURCE_LABEL: Record<NoteSource, string> = {
  agent: 'агент',
  manual: 'человек',
  import: 'импорт',
}

/** Полночь по местному времени — база для «сегодня/вчера».
 *  Сервер отдаёт метку времени и не знает часового пояса телефона, поэтому
 *  день считается здесь. */
function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

function dayLabel(ts: number): string {
  const now = new Date()
  const at = new Date(ts * 1000)
  const days = Math.round((startOfDay(now) - startOfDay(at)) / 86_400_000)
  if (days <= 0) return 'сегодня'
  if (days === 1) return 'вчера'
  return at.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    // Год дописываем только у прошлогодних дат: в ленте за неделю он лишний шум.
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
}

function timeLabel(ts: number): string {
  return new Date(ts * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

interface Group {
  label: string
  items: FeedItem[]
}

/** Записи по убыванию времени, разрезанные по календарным дням. */
function groupByDay(items: FeedItem[]): Group[] {
  const sorted = [...items].sort((a, b) => b.at - a.at)
  const groups: Group[] = []
  for (const item of sorted) {
    const label = dayLabel(item.at)
    const last = groups[groups.length - 1]
    if (last && last.label === label) last.items.push(item)
    else groups.push({ label, items: [item] })
  }
  return groups
}

function errorText(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message
  return 'Не удалось загрузить ленту'
}

interface Props {
  onOpen(path: string): void
}

export default function FeedScreen({ onOpen }: Props) {
  const [items, setItems] = useState<FeedItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** дёргается кнопкой «повторить» — перезапускает загрузку */
  const [reload, setReload] = useState(0)

  useEffect(() => {
    // Экран телефона живёт в стеке и уходит из монтирования вместе с табом:
    // применяем ответ, только если он ещё кому-то нужен.
    let alive = true
    setLoading(true)
    notesApi
      .feed(LIMIT)
      .then((page) => {
        if (!alive) return
        setItems(page.items)
        useAppStore.getState().setUnread(page.unread)
        setError(null)
        setLoading(false)
      })
      .catch((cause: unknown) => {
        if (!alive) return
        setError(errorText(cause))
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [reload])

  /** Отметить прочитанным (M7.1, M7.3). `paths` — ТОЛЬКО непрочитанные пути:
   *  на этом держится оптимистичный счётчик, и оба места вызова так и делают.
   *
   *  Идём через офлайн-очередь, а не прямо в API. Разница видна ровно там, где
   *  живёт телефон: без сети запрос мимо очереди умирает вместе с намерением —
   *  человек видел «прочитано», а после возврата связи точки на месте (M10.4).
   *  `queueAction` сначала кладёт действие в localStorage и только потом
   *  пробует выполнить, так что в онлайне разница лишь в одной записи.
   *
   *  Интерфейс обновляем сразу и не откатываем: и точки, и вычитание из бейджа
   *  идемпотентны, а точное число непрочитанных положит в стор `offline.ts`,
   *  когда очередь доиграется и сервер ответит. Ждать этого, чтобы открыть
   *  заметку, палец не должен. */
  const markRead = useCallback((paths: string[]) => {
    if (paths.length === 0) return
    const marked = new Set(paths)
    setItems((prev) =>
      prev.map((item) => (marked.has(item.path) ? { ...item, read: true } : item)),
    )
    const store = useAppStore.getState()
    store.setUnread(Math.max(0, store.unread - paths.length))
    queueAction({ kind: 'read', paths })
  }, [])

  /** Тап по карточке: отметка и открытие одним движением (M7.3).
   *  Отметку не ждём — заметка открывается сразу, а счётчик догонит. */
  const open = useCallback(
    (item: FeedItem) => {
      if (!item.read) markRead([item.path])
      onOpen(item.path)
    },
    [markRead, onOpen],
  )

  const groups = useMemo(() => groupByDay(items), [items])

  return (
    <div className="ab-mfeed">
      {error ? (
        <div className="ab-mfeed__error">
          <span>{error}</span>
          <button className="ab-mfeed__retry" onClick={() => setReload((n) => n + 1)}>
            повторить
          </button>
        </div>
      ) : null}

      {loading && items.length === 0 ? (
        <div className="ab-mfeed__note">загружаю ленту…</div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <div className="ab-mfeed__note">
          Пока ничего не менялось. Заметки, которые напишет агент или вы сами,
          появятся здесь.
        </div>
      ) : null}

      {groups.map((group) => {
        const unreadPaths = group.items.filter((it) => !it.read).map((it) => it.path)
        return (
          <section key={group.label} className="ab-mfeed__group">
            <div className="ab-mfeed__dayrow">
              <span className="ab-mfeed__day">{group.label}</span>
              {unreadPaths.length > 0 ? (
                <button
                  className="ab-mfeed__mark"
                  onClick={() => markRead(unreadPaths)}
                  aria-label={`отметить прочитанным: ${group.label}`}
                >
                  прочитано
                </button>
              ) : (
                <span className="ab-mfeed__done">прочитано</span>
              )}
            </div>

            {group.items.map((item) => (
              <button
                key={`${item.path}:${item.at}`}
                type="button"
                className={`ab-mcard${item.read ? ' is-read' : ''}`}
                onClick={() => open(item)}
              >
                <span className="ab-mcard__dot" aria-hidden="true" />
                <span className="ab-mcard__body">
                  <span className="ab-mcard__head">
                    <span className="ab-mcard__title">{item.title}</span>
                    <span className="ab-mcard__time">{timeLabel(item.at)}</span>
                  </span>
                  <span className={`ab-mcard__change is-${item.source}`}>
                    {CHANGE_LABEL[item.change]}
                  </span>
                  {item.summary ? (
                    <span className="ab-mcard__summary">{item.summary}</span>
                  ) : null}
                  <span className="ab-mcard__meta">
                    {SOURCE_LABEL[item.source]} · {item.path}
                  </span>
                </span>
              </button>
            ))}
          </section>
        )
      })}
    </div>
  )
}
